import { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AgentTab } from './AgentTab';
import { ToolsTab } from './ToolsTab';
import { PermissionsTab } from './PermissionsTab';
import { PermissionConfirmDialog } from './PermissionConfirmDialog';
import { MOCK_TOOLS } from './mockTools';
import {
  AgentBuilderProps,
  AgentTool,
  AgentPermission,
  permissionKey,
  isAllowableAgentPermission,
} from './types';

interface PendingToolConfirm {
  tool: AgentTool;
  missingPermissions: AgentPermission[];
}

export function AgentBuilder({ value, onChange }: AgentBuilderProps) {
  const [activeTab, setActiveTab] = useState('agent');
  const [pendingToolConfirm, setPendingToolConfirm] =
    useState<PendingToolConfirm | null>(null);

  const handleToolToggle = (
    toolId: string,
    enabled: boolean,
    requiredPermissions: AgentPermission[],
  ) => {
    if (enabled) {
      const tool = MOCK_TOOLS.find((t) => t.id === toolId);
      if (!tool) return;

      const permissionKeys = new Set(
        value.permissions.map((p) => permissionKey(p.action, p.subject)),
      );

      const missingPermissions = requiredPermissions.filter(
        (rp) => !permissionKeys.has(permissionKey(rp.action, rp.subject)),
      );

      if (missingPermissions.length > 0) {
        setPendingToolConfirm({ tool, missingPermissions });
      } else {
        const newEnabledTools = new Set(value.enabledTools);
        newEnabledTools.add(toolId);
        onChange({ ...value, enabledTools: newEnabledTools });
      }
    } else {
      const newEnabledTools = new Set(value.enabledTools);
      newEnabledTools.delete(toolId);
      onChange({ ...value, enabledTools: newEnabledTools });

      if (pendingToolConfirm?.tool.id === toolId) {
        setPendingToolConfirm(null);
      }
    }
  };

  const handleConfirmTool = () => {
    if (!pendingToolConfirm) return;

    const newEnabledTools = new Set(value.enabledTools);
    newEnabledTools.add(pendingToolConfirm.tool.id);

    const allowedMissingPermissions =
      pendingToolConfirm.missingPermissions.filter((p) =>
        isAllowableAgentPermission(p.action, p.subject),
      );

    const existingPermissionKeys = new Set(
      value.permissions.map((p) => permissionKey(p.action, p.subject)),
    );

    const newPermissions = [
      ...value.permissions,
      ...allowedMissingPermissions.filter(
        (p) => !existingPermissionKeys.has(permissionKey(p.action, p.subject)),
      ),
    ];

    onChange({
      ...value,
      enabledTools: newEnabledTools,
      permissions: newPermissions,
    });

    setPendingToolConfirm(null);
  };

  const handlePermissionsChange = (permissions: AgentPermission[]) => {
    const sanitizedPermissions = permissions.filter((p) =>
      isAllowableAgentPermission(p.action, p.subject),
    );
    onChange({ ...value, permissions: sanitizedPermissions });
  };

  return (
    <div className="w-full">
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="agent">Agent</TabsTrigger>
          <TabsTrigger value="tools">Tools</TabsTrigger>
          <TabsTrigger value="permissions">Permissions</TabsTrigger>
        </TabsList>

        <TabsContent value="agent" className="mt-4">
          <AgentTab
            name={value.name}
            description={value.description}
            prompt={value.prompt}
            onNameChange={(name) => onChange({ ...value, name })}
            onDescriptionChange={(description) =>
              onChange({ ...value, description })
            }
            onPromptChange={(prompt) => onChange({ ...value, prompt })}
          />
        </TabsContent>

        <TabsContent value="tools" className="mt-4">
          <ToolsTab
            enabledTools={value.enabledTools}
            permissions={value.permissions}
            onToolToggle={handleToolToggle}
          />
        </TabsContent>

        <TabsContent value="permissions" className="mt-4">
          <PermissionsTab
            permissions={value.permissions}
            onChange={handlePermissionsChange}
          />
        </TabsContent>
      </Tabs>

      <PermissionConfirmDialog
        open={pendingToolConfirm !== null}
        onOpenChange={(open) => {
          if (!open) setPendingToolConfirm(null);
        }}
        toolName={pendingToolConfirm?.tool.name ?? ''}
        requiredPermissions={pendingToolConfirm?.missingPermissions ?? []}
        onConfirm={handleConfirmTool}
        onCancel={() => setPendingToolConfirm(null)}
      />
    </div>
  );
}
